import styled from "styled-components";
import { useInventoryConfigGet } from "@dealer-center/ddc-pages-inventory-config-fetch";
import type { InventoryListingConfig } from "@dealer-center/ddc-pages-inventory-config-interface";
import { LoadingIndicator } from "@dealer-center/core-custom-components";
import { InputsList, configItemContext } from "./ConfigArea/inputList";
import { TopArea } from "./TopArea";
import { useEffect, useState } from "react";
import { ButtonBar } from "./ConfigArea/buttonBar";
import { updateValues } from "./ConfigArea/updateValues";
import { useNavigate, useParams } from "react-router-dom";
import { ImportModal } from "./TopArea/ImportModal";
import { Alert } from "@dealer-center/core-components";
import { useGetAccountId } from "@dealer-center/ddc-api-account-fetch";

const StyledTitleArea = styled.div`
  padding: 1rem 0;
  word-wrap: anywhere;
`;
const StyledResultsArea = styled.div`
  margin-bottom: 80px;
`;

export const PagesInventoryConfigUiDisplay = () => {
  const { selectedConfig: selectedConfigParam } = useParams();
  const [isNewConfig, setIsNewConfig] = useState(false);
  const dealerEntityId = useGetAccountId();

  if (!dealerEntityId) {
    throw new Error("No dealer entity id found");
  }

  const [selectedConfigName, setSelectedConfigName] = useState<
    string | undefined
  >(selectedConfigParam?.toLowerCase());
  const navigate = useNavigate();
  useEffect(() => {
    if (!selectedConfigName) return;
    if (selectedConfigParam === selectedConfigName) return;
    navigate(
      `/dealer/${dealerEntityId}/dashboard/websiteInventoryConfigs/${selectedConfigName}`
    );
  }, [dealerEntityId, navigate, selectedConfigName, selectedConfigParam]);

  const {
    data: selectedData,
    isFetching,
    isError
  } = useInventoryConfigGet({
    accountId: dealerEntityId,
    inventoryId: selectedConfigName || ""
  });
  const [importedConfig, setImportedConfig] = useState<
    InventoryListingConfig | undefined
  >(undefined);
  const [curentConfig, setCurrentConfig] = useState(selectedData);
  useEffect(() => {
    if (!selectedData) return;
    setCurrentConfig(selectedData);
  }, [selectedData]);
  useEffect(() => {
    if (!importedConfig) return;
    setCurrentConfig(importedConfig);
  }, [importedConfig]);

  const resetConfig = () => {
    setCurrentConfig(selectedData);
  };

  const setSelectedConfigNameLowercase = (configName: string) => {
    setSelectedConfigName(configName?.toLowerCase());
  };

  if (isError) {
    return <Alert type="error">Error loading inventory config details</Alert>;
  }
  const setCurrentConfigValue = updateValues(setCurrentConfig, curentConfig);
  return (
    <>
      <TopArea
        selectedConfigName={selectedConfigName}
        setIsNewConfig={setIsNewConfig}
        setSelectedConfigName={setSelectedConfigNameLowercase}
      />
      {isFetching && (
        <LoadingIndicator actionName="Loading inventory config details" />
      )}
      {!isFetching && curentConfig && selectedData && (
        <configItemContext.Provider
          value={{
            setIsNewConfig,
            isNewConfig,
            curentConfig,
            setCurrentConfigValue,
            selectedConfigName: selectedConfigName || "",
            setSelectedConfigName: setSelectedConfigNameLowercase,
            importedConfig,
            setImportedConfig,
            selectedConfig: selectedData,
            resetConfig
          }}
        >
          <ImportModal sx={{ mt: 2 }} />
          <StyledResultsArea>
            <StyledTitleArea>
              <h1>ID: {curentConfig.id}</h1>
              <h4>
                (Account ID:{curentConfig.accountId} | Inventory ID:
                {curentConfig.inventoryId})
              </h4>
            </StyledTitleArea>
            <InputsList />
          </StyledResultsArea>
          <ButtonBar />
        </configItemContext.Provider>
      )}
    </>
  );
};
